const {
  ALL_MODEL_TYPES,
  CATEGORY_MAP,
  DEFAULT_COMBINATION_RULES,
  DEFAULT_DISABLED_TYPES,
  DEFAULT_MIN_CONFIDENCE,
} = require('../constants/piiScan');

const FALLBACK_MASK_CATEGORIES = ['zip_code', 'number', 'id', 'account', 'role', 'website'];

function mapEntityTypeToCategory(type, settings) {
  if (settings?.categoryMap?.[type]) {
    return settings.categoryMap[type];
  }
  return CATEGORY_MAP[type] || 'number';
}

function normalizeConfidenceThreshold(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return DEFAULT_MIN_CONFIDENCE;
  }
  return Math.min(1, Math.max(0, n));
}

function getDefaultConfidenceThresholds(categoryMap) {
  return Object.fromEntries(
    Object.keys(categoryMap).map((type) => [type, DEFAULT_MIN_CONFIDENCE]),
  );
}

function normalizeConfidenceThresholds(raw, defaults, categoryMap) {
  const result = { ...defaults };
  if (raw && typeof raw === 'object') {
    for (const type of Object.keys(categoryMap)) {
      if (Object.prototype.hasOwnProperty.call(raw, type)) {
        result[type] = normalizeConfidenceThreshold(raw[type]);
      }
    }
  }
  for (const type of Object.keys(categoryMap)) {
    if (!(type in result)) {
      result[type] = DEFAULT_MIN_CONFIDENCE;
    }
  }
  return result;
}

function getDefaultPiiScanSettings() {
  const categoryMap = { ...CATEGORY_MAP };
  const enabledTypes = ALL_MODEL_TYPES.filter((type) => !DEFAULT_DISABLED_TYPES.has(type));
  return {
    enabledTypes,
    combinationRules: { ...DEFAULT_COMBINATION_RULES },
    categoryMap,
    confidenceThresholds: getDefaultConfidenceThresholds(categoryMap),
  };
}

function normalizePiiScanSettings(raw) {
  const defaults = getDefaultPiiScanSettings();
  if (!raw || typeof raw !== 'object') {
    return defaults;
  }
  const categoryMap = raw.categoryMap && typeof raw.categoryMap === 'object'
    ? { ...defaults.categoryMap, ...raw.categoryMap }
    : defaults.categoryMap;
  return {
    enabledTypes: Array.isArray(raw.enabledTypes) ? raw.enabledTypes : defaults.enabledTypes,
    combinationRules: raw.combinationRules && typeof raw.combinationRules === 'object'
      ? { ...defaults.combinationRules, ...raw.combinationRules }
      : defaults.combinationRules,
    categoryMap,
    confidenceThresholds: normalizeConfidenceThresholds(
      raw.confidenceThresholds,
      defaults.confidenceThresholds,
      categoryMap,
    ),
  };
}

function getConfiguredModelTypes(settings) {
  return Object.keys(settings.categoryMap).sort();
}

function getMaskCategories(settings) {
  const fromMap = [...new Set(
    Object.values(settings.categoryMap).filter((category) => category),
  )];
  return [...new Set([...fromMap, ...FALLBACK_MASK_CATEGORIES])];
}

function maskCategoryForEntity(entity, settings) {
  return entity.maskCategory || mapEntityTypeToCategory(entity.type, settings);
}

function getMaskCategoryOptions(entities = [], settings = getDefaultPiiScanSettings()) {
  const base = getMaskCategories(settings);
  const custom = [...new Set(
    entities
      .map((entity) => maskCategoryForEntity(entity, settings))
      .filter((category) => category && !base.includes(category)),
  )];
  return [...base, ...custom].map((value) => ({
    value,
    label: value,
  }));
}

function passesConfidenceThreshold(entity, settings) {
  const threshold = settings.confidenceThresholds?.[entity.type] ?? DEFAULT_MIN_CONFIDENCE;
  if (threshold <= 0) {
    return true;
  }
  if (entity.confidence == null) {
    return true;
  }
  return entity.confidence >= threshold;
}

/**
 * @param {object[]} entities
 * @param {ReturnType<typeof normalizePiiScanSettings>} settings
 */
function applyPiiScanSettings(entities, settings) {
  const enabled = new Set(settings.enabledTypes);
  const eligible = entities.filter(
    (entity) => enabled.has(entity.type) && passesConfidenceThreshold(entity, settings),
  );
  const presentTypes = new Set(eligible.map((e) => e.type));

  return eligible
    .filter((entity) => {
      const requires = settings.combinationRules[entity.type];
      if (!requires?.length) {
        return true;
      }
      return requires.some((type) => presentTypes.has(type));
    })
    .map((entity) => ({
      ...entity,
      maskCategory: mapEntityTypeToCategory(entity.type, settings),
    }));
}

// ponytail: simple regex, not a validator. Ceiling: misses unicode emails / exotic phone formats.
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Grouping/decimal marks: space (incl. NBSP via \s), ., ,, '. Hyphen/parens stay for phones.
const NUMBER_PATTERN = /(?<![\p{L}\p{N}])\+?\d[\d\s.,'()\-]*\d(?![\p{L}\p{N}])/gu;

function entityText(entity) {
  return entity.entity ?? entity.text ?? '';
}

function hasOffsets(entity) {
  return entity.start != null && entity.end != null && entity.end > entity.start;
}

function spanContains(outer, inner) {
  return outer.start <= inner.start && outer.end >= inner.end;
}

function spanLength(entity) {
  return entity.end - entity.start;
}

function trimNumberMatch(raw) {
  return raw.replace(/[^\d]+$/g, '');
}

function patternEntity({ type, maskCategory, text, start, end }) {
  return {
    type,
    maskCategory,
    entity: text,
    text,
    start,
    end,
    confidence: 1,
  };
}

function detectEmails(text) {
  return [...text.matchAll(EMAIL_PATTERN)].map((match) => patternEntity({
    type: 'EMAIL_ADDRESS',
    maskCategory: 'email',
    text: match[0],
    start: match.index,
    end: match.index + match[0].length,
  }));
}

function detectNumbers(text) {
  const found = [];
  for (const match of text.matchAll(NUMBER_PATTERN)) {
    const trimmed = trimNumberMatch(match[0]);
    const digits = trimmed.replace(/\D/g, '');
    const hasDelimiter = /[^\d+]/.test(trimmed);
    // Bare 4-digit tokens stay unflagged (years, 1234). Grouped amounts like 1.300 are kept.
    if (digits.length < 4 || (!hasDelimiter && digits.length < 5)) {
      continue;
    }
    found.push(patternEntity({
      type: 'PHONE_NUMBER',
      maskCategory: /[\s+\-()]/.test(trimmed) ? 'phone' : 'number',
      text: trimmed,
      start: match.index,
      end: match.index + trimmed.length,
    }));
  }
  return found;
}

/**
 * Simple email + number patterns for open answers the model misses (hyphenated
 * phones, policy numbers). No parsing beyond digit count and a basic email regex.
 * @param {string} text
 */
function detectPatternEntities(text) {
  if (typeof text !== 'string' || !text) {
    return [];
  }
  const emails = detectEmails(text);
  const numbers = detectNumbers(text).filter((number) => (
    !emails.some((email) => spanContains(email, number))
  ));
  return [...emails, ...numbers];
}

function spansOverlap(a, b) {
  return hasOffsets(a) && hasOffsets(b) && a.start < b.end && b.start < a.end;
}

function isCoveredBy(span, entities) {
  return entities.some((other) => hasOffsets(other) && spanContains(other, span));
}

function preferEntity(a, b) {
  const confA = a.confidence ?? 0;
  const confB = b.confidence ?? 0;
  if (confA !== confB) {
    return confA > confB ? a : b;
  }
  const lenA = hasOffsets(a) ? spanLength(a) : entityText(a).length;
  const lenB = hasOffsets(b) ? spanLength(b) : entityText(b).length;
  if (lenA !== lenB) {
    return lenA > lenB ? a : b;
  }
  return a;
}

function dedupePiiEntitySpans(entities) {
  const withOffsets = [];
  const withoutOffsets = [];
  for (const entity of entities) {
    if (hasOffsets(entity)) {
      withOffsets.push(entity);
    } else {
      withoutOffsets.push(entity);
    }
  }

  const bySpan = new Map();
  for (const entity of withOffsets) {
    const key = `${entity.start}:${entity.end}`;
    const existing = bySpan.get(key);
    bySpan.set(key, existing ? preferEntity(existing, entity) : entity);
  }
  const unique = [...bySpan.values()];

  const kept = unique.filter((entity, index) => !unique.some((other, otherIndex) => (
    otherIndex !== index
    && spanContains(other, entity)
    && spanLength(other) > spanLength(entity)
  )));

  return [...kept, ...withoutOffsets].sort((a, b) => {
    const startDiff = (a.start ?? Infinity) - (b.start ?? Infinity);
    if (startDiff !== 0) {
      return startDiff;
    }
    return spanLength(b) - spanLength(a);
  });
}

function appendPatternEntities(text, entities) {
  const existing = Array.isArray(entities) ? entities : [];
  const patterns = detectPatternEntities(text);
  const emails = patterns.filter((pattern) => pattern.type === 'EMAIL_ADDRESS');
  const numbers = patterns.filter((pattern) => pattern.type !== 'EMAIL_ADDRESS');

  // Regex emails win: drop any model span that overlaps the full address so split
  // local-part / domain / TLD hits (or a covering account span) become one mask.
  const withoutEmailOverlap = existing.filter((entity) => (
    !emails.some((email) => spansOverlap(entity, email) || spanContains(email, entity))
  ));
  const uncoveredNumbers = numbers.filter((number) => (
    !isCoveredBy(number, withoutEmailOverlap)
    && !emails.some((email) => spansOverlap(number, email) || spanContains(email, number))
  ));
  return dedupePiiEntitySpans([...withoutEmailOverlap, ...emails, ...uncoveredNumbers]);
}

/**
 * Filter model entities by settings, then add simple number/email pattern matches.
 * @param {object[]} entities
 * @param {ReturnType<typeof normalizePiiScanSettings>} settings
 * @param {string} [text]
 */
function processPiiEntities(entities, settings, text) {
  const filtered = applyPiiScanSettings(entities, settings);
  if (typeof text !== 'string' || !text) {
    return dedupePiiEntitySpans(filtered);
  }
  return appendPatternEntities(text, filtered);
}

module.exports = {
  mapEntityTypeToCategory,
  getDefaultPiiScanSettings,
  normalizePiiScanSettings,
  getConfiguredModelTypes,
  getMaskCategories,
  maskCategoryForEntity,
  getMaskCategoryOptions,
  applyPiiScanSettings,
  detectPatternEntities,
  dedupePiiEntitySpans,
  appendPatternEntities,
  processPiiEntities,
  passesConfidenceThreshold,
  normalizeConfidenceThreshold,
};
